import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { MenuItem } from '@lib/rs-ngx';
import { CurrentUser } from '@shared/models/user';
import * as Sentry from '@sentry/angular';
import { selectCurrentRsUser } from '@app/core/state/user/user.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FRONT_PLATE_LEASING_COMPANY_NUMBERS } from '@shared/guards/front-plate/front-plate.guard';
import { RsTopBarMenuComponent } from '@rs-shared-lib/components/topbar/rs-top-bar-menu.component';
import { RsFooterComponent } from '@rs-shared-lib/components/footer/rs-footer.component';
import { RouterOutlet } from '@angular/router';
import { RsLoaderComponent } from '@rs-shared-lib/components/rs-loader/components/rs-loader.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    RsTopBarMenuComponent,
    RsFooterComponent,
    RouterOutlet,
    RsLoaderComponent
  ]
})
export class AppComponent implements OnInit {
  public menuItems = signal<MenuItem[]>([]);
  private readonly destroyRef$ = inject(DestroyRef);
  private readonly store = inject(Store);
  private currentUser?: CurrentUser;

  public constructor() {
    if ('Cypress' in window) {
      window['store'] = this.store;
    }
  }

  public onRouteChange(url: string): void {
    if (url === '/rs-number') {
      this.menuItems.set([]);
    } else {
      this.setTopBarMenu();
    }
  }

  public ngOnInit(): void {
    this.store
      .select(selectCurrentRsUser)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe((currentUser) => {
        if (currentUser) {
          this.currentUser = currentUser;
          Sentry.getCurrentScope().setUser({
            rsUser: {
              companyName: currentUser.companyName,
              loginString: currentUser.loginString,
              userRole: currentUser.userRole,
              id: currentUser.id
            }
          });
        }
      });
  }

  public onLogout(): void {
    localStorage.clear();
  }

  private setTopBarMenu(): void {
    // Do not reset if already set
    if (this.menuItems().length === 0) {
      this.menuItems.set([
        this.getManagementMenu(),
        this.getUsersMenu(),
        this.getOrdersMenu(),
        this.getRegistrationMenu(),
        this.getDistributionCenterMenu(),
        this.getAdministrationMenu(),
        this.getRsNumberMenu()
      ]);
    }
  }

  private getManagementMenu(): MenuItem {
    return {
      id: 'management',
      label: 'MANAGEMENT',
      display: this.currentUser?.userRole === 'ADMIN',
      subMenuItems: [
        {
          url: 'admin/suppliers',
          id: 'admin-suppliers',
          label: 'SUPPLIERS'
        },
        {
          url: 'admin/leasing-companies',
          id: 'admin-leasing-companies',
          label: 'LEASING_COMPANIES'
        },
        {
          url: 'admin/brokers',
          id: 'admin-brokers',
          label: 'BROKERS'
        }
      ]
    };
  }

  private getUsersMenu(): MenuItem {
    return {
      id: 'admin-users',
      label: 'USERS',
      display: this.currentUser?.userRole === 'ADMIN',
      url: 'admin/users'
    };
  }

  private getOrdersMenu(): MenuItem {
    const showVehicles = this.currentUser?.access.vehicles;
    const showEVProducts = this.currentUser?.access?.wallBox;
    const showComplementaryOrders = this.currentUser?.access.complementaryOrder;

    return {
      id: 'active-orders-overview',
      label: 'ORDERS',
      url: '/orders/active-orders-overview',
      baseMenuUrl: ['orders', 'ev-products', 'complementary-orders'],
      display: this.currentUser?.userRole != 'ADMIN',
      subMenuItems: [
        {
          url: '/orders/active-orders-overview',
          id: 'orders-active-orders-overview',
          label: 'VEHICLES',
          display: (showVehicles && showEVProducts) || (showVehicles && showComplementaryOrders)
        },
        {
          url: 'ev-products',
          id: 'ev-products',
          display: showVehicles && showEVProducts,
          label: 'EV_PRODUCTS'
        },
        {
          id: 'complementary-orders',
          url: 'complementary-orders',
          label: 'COMPLEMENTARY_ORDERS',
          display: showVehicles && showComplementaryOrders
        }
      ]
    };
  }

  private getRegistrationMenu(): MenuItem {
    return {
      id: 'active-registrations-overview',
      label: 'REGISTRATION',
      display: this.currentUser?.access?.registration || this.currentUser?.access?.distributionCenter,
      baseMenuUrl: ['registrations', 'front-plates'],
      subMenuItems: [
        {
          id: 'registrations-overview-active',
          url: 'registrations/active',
          display: this.currentUser?.access?.registration,
          label: 'REGISTRATIONS'
        },
        {
          url: 'front-plates',
          display: this.shouldDisplayFrontPlatesForCurrentUser(),
          id: 'front-plates',
          label: 'FRONT_PLATES'
        },
      ]
    };
  }

  private getDistributionCenterMenu(): MenuItem {
    const isLeasingCompanyWithAuthorization = this.currentUser!.userRole === 'LEASING_COMPANY_WITH_AUTHORIZATION';
    const isLeasingCompany = this.currentUser!.userRole === 'LEASING_COMPANY';
    const hasAccessToDistributionCenter = this.currentUser!.access.distributionCenter || false;
    const canDisplayDistributionCenterMenu = this.currentUser!.access.distributionCenterVersion === 'VERSION_1' ? isLeasingCompanyWithAuthorization : true;

    return {
      id: 'distribution-center',
      label: 'DISTRIBUTION_CENTER',
      display: canDisplayDistributionCenterMenu && hasAccessToDistributionCenter,
      subMenuItems: [
        {
          id: 'distribution-center-overview-items',
          url: 'distribution-center/items',
          label: 'ITEMS',
          display: isLeasingCompanyWithAuthorization
        },
        {
          id: 'distribution-center-overview-packages',
          url: 'distribution-center/packages',
          label: 'PACKAGES',
          display: isLeasingCompanyWithAuthorization
        },
        {
          id: 'distribution-center-overview-packages-orders',
          url: 'distribution-center/package-orders',
          label: 'PACKAGE-ORDERS',
          display: this.currentUser?.access.distributionCenterVersion === 'VERSION_2' && (isLeasingCompany || isLeasingCompanyWithAuthorization)
        },
        {
          id: 'distribution-center-anomalies',
          url: 'distribution-center/anomalies',
          label: 'ANOMALIES',
          display: this.currentUser?.access.distributionCenterVersion === 'VERSION_2' && (isLeasingCompany || isLeasingCompanyWithAuthorization)
        }
      ]
    };
  }

  private shouldDisplayFrontPlatesForCurrentUser(): boolean {
    return FRONT_PLATE_LEASING_COMPANY_NUMBERS.some(lcNumber => this.currentUser?.rsNumber === lcNumber);
  }

  private getAdministrationMenu(): MenuItem {
    return {
      url: 'administration',
      id: 'administration',
      label: 'ADMINISTRATION',
      display: this.currentUser && this.currentUser?.userRole === 'LEASING_COMPANY_WITH_AUTHORIZATION',
    };
  }

  private getRsNumberMenu(): MenuItem {
    return {
      url: 'rs-number',
      id: 'rs-number',
      label: 'SWITCH_ACCOUNT',
      display: this.currentUser?.hasAccessToRsNumberSelectionPage,
      labelSuffix: '(' + this.currentUser?.loginString + ')'
    };
  }
}
